/**
 * Map category axis labels to picklist path_color from field metadata (report / summarizedData._metadata.group_fields).
 */

const DEFAULT_ALT_COLORS = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#4BC0C0',
  '#9966FF'
];

/**
 * @param {Array<object>|undefined} picklistValues — API/report often use value_key / value_label;
 *   layout & PathComponent use key / value / label (see object.model _picklistValuesJsonForLayoutField).
 * @returns {Map<string, string>} Map of each identifier string to path color
 */
export function buildPicklistValueColorMap(picklistValues) {
  const map = new Map();
  if (!Array.isArray(picklistValues)) return map;
  for (const pv of picklistValues) {
    if (!pv) continue;
    const raw = pv.path_color != null && pv.path_color !== '' ? pv.path_color : pv.pathColor;
    if (raw == null || raw === '') continue;
    const col = String(raw).trim();
    // Report / reportbuilder field metadata
    if (pv.value_key != null) map.set(String(pv.value_key), col);
    if (pv.value_label != null) map.set(String(pv.value_label), col);
    // Layout path JSON: { key, value, path_color } (value = display label)
    if (pv.key != null) map.set(String(pv.key), col);
    if (pv.value != null) map.set(String(pv.value), col);
    if (pv.label != null) map.set(String(pv.label), col);
  }
  return map;
}

/**
 * @param {string} label - Group key as shown on chart (display value or value key)
 * @param {Map<string, string>} colorMap
 * @param {number} index - Fallback palette index
 * @param {string} [fieldFallback] - When no picklist color for label
 * @returns {string}
 */
export function colorForLabelFromPicklistMap(label, colorMap, index, fieldFallback) {
  if (!colorMap || colorMap.size === 0) return fieldFallback;
  const s = String(label);
  if (colorMap.has(s)) return colorMap.get(s);
  for (const [k, v] of colorMap.entries()) {
    if (String(k).trim() === s.trim()) return v;
  }
  return fieldFallback != null ? fieldFallback : DEFAULT_ALT_COLORS[index % DEFAULT_ALT_COLORS.length];
}

/**
 * One color per category (label) for bar/line point/pie slice when the group field is picklist.
 * @param {string[]} labels
 * @param {object|undefined} groupFieldInfo - metadata.group_fields[fieldName]
 * @param {string|undefined} valueFieldColor - default field color when not picklist or unmapped
 * @param {number} [valueFieldIndex] - for DEFAULT_ALT_COLORS
 * @returns {string[]|null} - null = use single field color, not per-category
 */
export function getCategoryAxisColorsForChart(labels, groupFieldInfo, valueFieldColor, valueFieldIndex = 0) {
  if (!groupFieldInfo || !Array.isArray(labels) || labels.length === 0) return null;
  // Prefer any picklist row with path_color; do not require data_type (path fields still picklist in DB;
  // some payloads omit or vary data_type on group metadata).
  const map = buildPicklistValueColorMap(groupFieldInfo.picklist_values);
  if (map.size === 0) return null;
  const fieldFallback = valueFieldColor || DEFAULT_ALT_COLORS[valueFieldIndex % DEFAULT_ALT_COLORS.length];
  return labels.map((lab, i) => colorForLabelFromPicklistMap(lab, map, i, fieldFallback));
}

/**
 * @param {string} hexOrCss
 * @param {string} alphaHex - e.g. '40' for ChartDisplay area fill hack
 * @returns {string}
 */
export function appendAlphaIfHex(hexOrCss, alphaHex = '40') {
  if (typeof hexOrCss !== 'string') return hexOrCss;
  const t = hexOrCss.trim();
  if (t.startsWith('#') && t.length === 7) return `${t}${alphaHex}`;
  return t;
}

export { DEFAULT_ALT_COLORS };
